/*
  What a board in this trade actually asks for.

  The Numbers used to say "upload a statement and the figures land here", which is true and
  useless: it does not tell an owner which figures matter, so the ones that arrive are
  whichever ones the accountant happened to print. A restaurant board that never sees prime
  cost is reasoning about a restaurant with its eyes shut.

  Two rules for anything added here. Every entry is a number the owner of that kind of
  business already tracks or already knows they should, not a metric invented for a
  dashboard. And `why` says what decision the number changes, because a list of KPIs with no
  reason attached reads as homework.

  `match` decides whether the figure is already on file. It is tested against the metric key
  and the label, so it has to be loose: figures arrive from a P&L a model transcribed, and
  "Food cost" and "Cost of food sold" are the same line.
*/

export interface Kpi {
  name: string
  why: string
  match: RegExp
  /** What the owner will type. Decides the field's prefix, suffix and sanity check. */
  unit: 'usd' | 'percent' | 'count'
  /** The canonical key the figure is stored under, so two spellings do not become two metrics. */
  metric: string
}

/** Any business, whatever it does. The floor, not the ceiling. */
const UNIVERSAL: Kpi[] = [
  {
    name: 'Revenue',
    unit: 'usd',
    metric: 'revenue',
    why: 'Everything else is a proportion of it.',
    match: /revenue|turnover|total sales|gross sales/i,
  },
  {
    name: 'Gross margin',
    unit: 'percent',
    metric: 'gross_margin_pct',
    why: 'What is left after the cost of delivering the work, before overhead.',
    match: /gross (profit|margin)/i,
  },
  {
    name: 'Net profit',
    unit: 'usd',
    metric: 'net_profit',
    why: 'What the year was actually worth to you.',
    match: /net (profit|income)|ebitda/i,
  },
  {
    name: 'Payroll as a share of revenue',
    unit: 'percent',
    metric: 'payroll_pct',
    why: 'The largest cost in most businesses, and the slowest to correct.',
    match: /payroll|wages|labou?r/i,
  },
  {
    name: 'Cash on hand',
    unit: 'usd',
    metric: 'cash_on_hand',
    why: 'Decides how long you can be wrong for.',
    match: /cash|bank balance|runway/i,
  },
  /*
    Aged debt and unbilled work. Both universal, both previously missing, and the pair of
    them is why a firm could hand over two documents stating the same overdue figure five
    percent apart and be told nothing. Without a key of their own they arrived as Other,
    which groups on free text, so "Over 90 days receivable" and "90+ days overdue" never
    met. The reasoning is in TradeKpis.cs, which this file mirrors.
  */
  {
    name: 'Receivable over ninety days',
    unit: 'usd',
    metric: 'receivables_over_90',
    why: 'The part of the ledger that stops being money and starts being an argument.',
    match: /90\+|ninety day|over 90|aged debt|past due/i,
  },
  {
    name: 'Unbilled work in progress',
    unit: 'usd',
    metric: 'work_in_progress',
    why: 'Work you have already paid for and not yet asked to be paid for.',
    match: /work in progress|\bwip\b|unbilled/i,
  },
  /*
    Money you are holding and do not own. Kept apart from cash on hand because a firm was
    shown as having 1,125,360 when 1,125,360 was its clients' money and its own operating
    account held 312,000. The reasoning is in TradeKpis.cs, which this file mirrors.
  */
  {
    name: 'Client money held',
    unit: 'usd',
    metric: 'client_funds_held',
    why: 'Someone else’s money, and never a sign that you are comfortable.',
    match: /trust account|client account|escrow|deposits held|retainers held/i,
  },
  // Asked for by the setup run in every trade. Mirrors TradeKpis.cs.
  {
    name: 'Marketing spend',
    unit: 'usd',
    metric: 'marketing_spend',
    why: 'Whether leads are expensive, which no other figure can tell you.',
    match: /marketing|advertising|ad spend|lead gen/i,
  },
]

interface Family {
  match: RegExp
  label: string
  kpis: Kpi[]
}

const FAMILIES: Family[] = [
  {
    match: /restaurant|cafe|café|coffee|bakery|bar\b|pub|food|catering|kitchen|pizz|diner|bistro|grill|deli/i,
    label: 'restaurant',
    kpis: [
      {
        name: 'Food cost percentage',
        unit: 'percent',
        metric: 'food_cost_pct',
        why: 'Food cost over food sales. Decides whether the menu prices work at all.',
        match: /food cost|cost of food/i,
      },
      {
        name: 'Labour cost percentage',
        unit: 'percent',
        metric: 'labour_cost_pct',
        why: 'Labour over total sales. The other half of the only ratio that matters.',
        match: /labou?r (cost|percent)|payroll/i,
      },
      {
        name: 'Prime cost',
        unit: 'percent',
        metric: 'prime_cost_pct',
        why: 'Food plus beverage plus labour, as a share of sales. Most operators run the '
           + 'whole business off this one number and want it under about 65%.',
        match: /prime cost/i,
      },
      {
        name: 'Average check',
        unit: 'usd',
        metric: 'average_check',
        why: 'Revenue over covers. Moves with the menu, not with how busy you were.',
        match: /average (check|cheque|spend|ticket)|spend per head/i,
      },
      {
        name: 'Covers',
        unit: 'count',
        metric: 'covers',
        why: 'How many people you actually served. Separates a good night from a good price.',
        match: /covers|guest count|customers served/i,
      },
      {
        name: 'Beverage cost percentage',
        unit: 'percent',
        metric: 'beverage_cost_pct',
        why: 'Usually the highest-margin thing you sell, and the easiest to lose to pouring.',
        match: /beverage cost|bar cost|liquor cost|drink cost/i,
      },
    ],
  },
  {
    match: /plumb|hvac|electric|roof|construct|contract|landscap|excavat|paint|mechanic|garage|auto|trade|install|repair/i,
    label: 'trades',
    kpis: [
      {
        name: 'Gross margin per job',
        unit: 'percent',
        metric: 'job_margin_pct',
        why: 'A busy month at a bad margin is how trades businesses fail while growing.',
        match: /gross (profit|margin)|job margin/i,
      },
      {
        name: 'Close rate',
        unit: 'percent',
        metric: 'close_rate_pct',
        why: 'Quotes won over quotes given. Tells you whether the problem is leads or pricing.',
        match: /close rate|win rate|conversion/i,
      },
      {
        name: 'Average job value',
        unit: 'usd',
        metric: 'average_job_value',
        why: 'Two ways to grow, and this is the one that does not need more vans.',
        match: /average (job|ticket|invoice|order)/i,
      },
      {
        name: 'Billable hours ratio',
        unit: 'percent',
        metric: 'billable_ratio_pct',
        why: 'Hours on a job over hours paid for. Where margin leaks without anyone noticing.',
        match: /billable|utilisation|utilization|productive hours/i,
      },
      {
        name: 'Callback rate',
        unit: 'percent',
        metric: 'callback_rate_pct',
        why: 'Work done twice, paid once. The clearest read on quality you have.',
        match: /callback|rework|warranty|redo/i,
      },
      {
        name: 'Revenue per van',
        unit: 'usd',
        metric: 'revenue_per_van',
        why: 'What a truck has to earn before another one is worth buying.',
        match: /per (van|truck|crew|tech)/i,
      },
    ],
  },
  {
    match: /retail|shop|store|boutique|grocer|market|apparel|florist|e-?commerce/i,
    label: 'retail',
    kpis: [
      {
        name: 'Gross margin',
        unit: 'percent',
        metric: 'gross_margin_pct',
        why: 'After cost of goods. The number every buying decision moves.',
        match: /gross (profit|margin)/i,
      },
      {
        name: 'Inventory turns',
        unit: 'count',
        metric: 'inventory_turns',
        why: 'How many times the stock sells through in a year. Cash asleep on the shelf.',
        match: /inventory turn|stock turn|turns/i,
      },
      {
        name: 'Average transaction value',
        unit: 'usd',
        metric: 'average_transaction_value',
        why: 'Cheaper to raise than footfall is.',
        match: /average (transaction|basket|order|sale)|atv|aov/i,
      },
      {
        name: 'Conversion rate',
        unit: 'percent',
        metric: 'conversion_rate_pct',
        why: 'Buyers over visitors. Separates a traffic problem from a shop-floor problem.',
        match: /conversion|footfall conversion/i,
      },
      {
        name: 'Sales per square foot',
        unit: 'usd',
        metric: 'sales_per_sq_ft',
        why: 'What the space earns, which is what the lease is priced against.',
        match: /per square (foot|metre|meter)|sales per sq/i,
      },
      {
        name: 'Shrinkage',
        unit: 'percent',
        metric: 'shrinkage_pct',
        why: 'Stock that left without being sold. Small, constant, and never in the P&L by name.',
        match: /shrink|stock loss|theft/i,
      },
    ],
  },
  {
    match: /salon|spa|barber|groom|fitness|gym|studio|clinic|dental|therap|veterinar|wellness|massage/i,
    label: 'appointment-based',
    kpis: [
      {
        name: 'Rebooking rate',
        unit: 'percent',
        metric: 'rebooking_rate_pct',
        why: 'Clients who book the next one before leaving. The whole business, in one number.',
        match: /rebook|repeat|retention/i,
      },
      {
        name: 'Chair or room utilisation',
        unit: 'percent',
        metric: 'utilisation_pct',
        why: 'Booked hours over available hours. What you are actually selling is time.',
        match: /utilisation|utilization|occupancy|chair|room/i,
      },
      {
        name: 'Average ticket',
        unit: 'usd',
        metric: 'average_ticket',
        why: 'Revenue per visit. Moves with what gets added on, not with how full you are.',
        match: /average (ticket|check|spend|sale)/i,
      },
      {
        name: 'No-show rate',
        unit: 'percent',
        metric: 'no_show_rate_pct',
        why: 'Unsellable time, usually never counted and almost never charged for.',
        match: /no.?show|cancellation/i,
      },
      {
        name: 'Revenue per practitioner',
        unit: 'usd',
        metric: 'revenue_per_practitioner',
        why: 'What a chair has to earn to be worth the space and the pay.',
        match: /per (practitioner|stylist|therapist|provider|staff)/i,
      },
      {
        name: 'New client count',
        unit: 'count',
        metric: 'new_clients',
        why: 'The only thing that offsets the clients everyone quietly loses each year.',
        match: /new client|new customer|acquisition/i,
      },
    ],
  },
  {
    match: /agenc|consult|account|legal|law|architec|engineer|design|marketing|software|it services|recruit/i,
    label: 'professional services',
    kpis: [
      {
        name: 'Utilisation rate',
        unit: 'percent',
        metric: 'utilisation_pct',
        why: 'Billable hours over available hours. The margin lives here.',
        match: /utilisation|utilization|billable/i,
      },
      {
        name: 'Realisation rate',
        unit: 'percent',
        metric: 'realisation_pct',
        why: 'Billed over worked. What you write off before the invoice goes out.',
        match: /realisation|realization|write.?off|recovery/i,
      },
      {
        name: 'Effective hourly rate',
        unit: 'usd',
        metric: 'effective_hourly_rate',
        why: 'Fee over hours actually spent. Fixed-price work hides the truth until this is run.',
        match: /effective (hourly|rate)|per hour/i,
      },
      {
        name: 'Revenue per employee',
        unit: 'usd',
        metric: 'revenue_per_employee',
        why: 'Whether growth is adding leverage or just adding people.',
        match: /per (employee|head|fte)/i,
      },
      {
        name: 'Client concentration',
        unit: 'percent',
        metric: 'client_concentration_pct',
        why: 'Share of revenue from the largest client. How exposed one phone call makes you.',
        match: /concentration|largest client|top client/i,
      },
      {
        name: 'Pipeline or backlog',
        unit: 'usd',
        metric: 'pipeline_value',
        why: 'Booked work ahead. The only forward-looking number on this list.',
        match: /pipeline|backlog|booked/i,
      },
    ],
  },
]

export interface KpiSet {
  /** Names the family in a sentence, e.g. "a restaurant board". Null for the generic set. */
  label: string | null
  kpis: Kpi[]
}

/**
 * The KPIs worth asking a business of this trade for: its own family first, then the
 * universal ones it has not already covered.
 */
export function kpisFor(trade: string | null | undefined): KpiSet {
  const family = trade ? FAMILIES.find(f => f.match.test(trade)) : undefined
  if (!family) return { label: null, kpis: UNIVERSAL }

  // A restaurant is still a business, so revenue and net profit still matter. Anything the
  // family already covers in its own words is dropped rather than asked for twice.
  const extra = UNIVERSAL.filter(u => !family.kpis.some(k => k.match.source === u.match.source
    && k.name === u.name) && !family.kpis.some(k => u.match.test(k.name)))

  return { label: family.label, kpis: [...family.kpis, ...extra] }
}
